/**
 * Homepage Carousel with Swiper.js
 * Handles carousel initialization with click-to-advance functionality
 * Click anywhere on the slide to advance to the next slide (like product carousel)
 * Updates header text, color, and logo based on active slide
 */

(function() {
  'use strict';

  // Store current carousel state to protect against external modifications
  let carouselState = {
    headerText: '',
    headerTextLink: '',
    headerColor: 'black',
    logoColor: 'black',
    isActive: false
  };

  // Store swiper instance for resize handling
  let swiperInstance = null;
  let resizeTimeout = null;

  function waitForSwiper(callback, maxAttempts = 50) {
    let attempts = 0;
    const checkSwiper = setInterval(() => {
      attempts++;
      if (typeof Swiper !== 'undefined') {
        clearInterval(checkSwiper);
        callback();
      } else if (attempts >= maxAttempts) {
        clearInterval(checkSwiper);
        console.error('Swiper.js failed to load after ' + maxAttempts + ' attempts');
      }
    }, 100);
  }

  function initCarousel() {
    const carouselElement = document.querySelector('[data-homepage-carousel]');
    if (!carouselElement) return;

    if (typeof Swiper === 'undefined') {
      waitForSwiper(initCarousel);
      return;
    }

    // Mark carousel as active
    carouselState.isActive = true;

    swiperInstance = new Swiper(carouselElement, {
      speed: 0, // Instant transition
      loop: true, // Infinite loop
      allowTouchMove: false, // No swipe, click only
      slidesPerView: 1,
      spaceBetween: 0,
      on: {
        init: function() {
          // Small delay to ensure Swiper has applied active class
          const swiper = this;
          setTimeout(function() {
            updateSlideState(swiper);
            // Set up protection after initial state is set
            setupHeaderTextProtection();
          }, 10);
        },
        // Use slideChangeTransitionEnd instead of slideChange
        // This fires AFTER the transition completes, ensuring we get the correct slide
        slideChangeTransitionEnd: function() {
          updateSlideState(this);
        },
      },
    });

    // Mouse drag handling: click and drag to change slides
    let mouseStartY = 0;
    let mouseStartX = 0;
    let isDragging = false;
    let hasDragChangedSlide = false;
    const dragThreshold = 30; // minimum drag distance in px

    carouselElement.addEventListener('mousedown', (e) => {
      // Don't start drag on buttons or links
      if (e.target.closest('button') || e.target.closest('a')) return;
      
      // Prevent default to stop image drag behavior
      e.preventDefault();
      
      isDragging = true;
      hasDragChangedSlide = false;
      mouseStartY = e.clientY;
      mouseStartX = e.clientX;
    });

    document.addEventListener('mousemove', (e) => {
      if (!isDragging || hasDragChangedSlide) return;

      const deltaY = mouseStartY - e.clientY;
      const deltaX = mouseStartX - e.clientX;

      // Only trigger if vertical drag is dominant and exceeds threshold
      if (Math.abs(deltaY) > Math.abs(deltaX) && Math.abs(deltaY) >= dragThreshold) {
        hasDragChangedSlide = true;

        if (deltaY > 0) {
          swiperInstance.slideNext();
        } else {
          swiperInstance.slidePrev();
        }
      }
    });

    document.addEventListener('mouseup', (e) => {
      if (!isDragging) return;
      
      const wasDragging = hasDragChangedSlide;
      isDragging = false;
      hasDragChangedSlide = false;

      // If we didn't drag enough to change slide, treat as click
      if (!wasDragging) {
        const deltaY = Math.abs(mouseStartY - e.clientY);
        const deltaX = Math.abs(mouseStartX - e.clientX);
        // Only advance on click if movement was minimal (not a failed drag)
        if (deltaY < 5 && deltaX < 5) {
          if (e.target.closest('.swiper-slide') && 
              !e.target.closest('button') && 
              !e.target.closest('a')) {
            swiperInstance.slideNext();
          }
        }
      }
    });

    // Keep pointer cursor
    carouselElement.style.cursor = 'pointer';
    
    // Prevent text/image selection during drag
    carouselElement.style.userSelect = 'none';
    carouselElement.style.webkitUserSelect = 'none';
    
    // Prevent native image dragging
    carouselElement.querySelectorAll('img').forEach(img => {
      img.draggable = false;
    });
    carouselElement.addEventListener('dragstart', (e) => {
      e.preventDefault();
    });

    // Track if mouse is over carousel for wheel navigation
    // Initialize as true since carousel is full-screen and mouse likely starts over it
    let isMouseOverCarousel = true;
    
    carouselElement.addEventListener('mouseenter', () => {
      isMouseOverCarousel = true;
    });
    
    carouselElement.addEventListener('mouseleave', () => {
      isMouseOverCarousel = false;
    });

    // Scroll gesture detection: advance one slide per discrete scroll action
    // Detects when scroll "stops" (no events for ~80ms) to allow next action
    let scrollEndTimeout = null;
    let hasChangedSlide = false;
    const scrollEndDelay = 80; // ms of no wheel events = scroll gesture ended
    const deltaThreshold = 5; // minimum delta to trigger (filters tiny movements)

    // Handle wheel/scroll to change slides when cursor is over carousel
    carouselElement.addEventListener('wheel', (e) => {
      if (!isMouseOverCarousel) return;

      // Prevent page scroll when scrolling over carousel
      e.preventDefault();

      // Clear any existing "scroll ended" timeout
      clearTimeout(scrollEndTimeout);

      // If we already changed slide during this scroll gesture, ignore until gesture ends
      if (hasChangedSlide) {
        // Set timeout to detect when scrolling stops
        scrollEndTimeout = setTimeout(() => {
          hasChangedSlide = false;
        }, scrollEndDelay);
        return;
      }

      // Check if delta exceeds threshold
      if (Math.abs(e.deltaY) >= deltaThreshold) {
        hasChangedSlide = true;

        // Determine direction and change slide
        if (e.deltaY > 0) {
          swiperInstance.slideNext();
        } else {
          swiperInstance.slidePrev();
        }
      }

      // Set timeout to detect when scrolling stops
      scrollEndTimeout = setTimeout(() => {
        hasChangedSlide = false;
      }, scrollEndDelay);
    }, { passive: false });

    // Touch/swipe handling: one swipe = one slide
    // Each touch gesture (touchstart → touchend) can only trigger one slide change
    let touchStartY = 0;
    let touchStartX = 0;
    const touchThreshold = 50; // minimum swipe distance in px

    carouselElement.addEventListener('touchstart', (e) => {
      touchStartY = e.touches[0].clientY;
      touchStartX = e.touches[0].clientX;
    }, { passive: true });

    carouselElement.addEventListener('touchmove', (e) => {
      // Prevent default scrolling while on carousel
      e.preventDefault();
    }, { passive: false });

    carouselElement.addEventListener('touchend', (e) => {
      const touchEndY = e.changedTouches[0].clientY;
      const touchEndX = e.changedTouches[0].clientX;
      const deltaY = touchStartY - touchEndY;
      const deltaX = touchStartX - touchEndX;

      // Only trigger if vertical swipe is dominant and exceeds threshold
      if (Math.abs(deltaY) > Math.abs(deltaX) && Math.abs(deltaY) >= touchThreshold) {
        if (deltaY > 0) {
          // Swipe up - next slide
          swiperInstance.slideNext();
        } else {
          // Swipe down - previous slide
          swiperInstance.slidePrev();
        }
      }
    }, { passive: true });

    // Listen for resize to update colors when crossing mobile/desktop breakpoint
    window.addEventListener('resize', handleResize);
  }

  /**
   * Debounced resize handler to update colors when viewport changes
   */
  function handleResize() {
    if (resizeTimeout) {
      clearTimeout(resizeTimeout);
    }
    resizeTimeout = setTimeout(function() {
      if (swiperInstance && carouselState.isActive) {
        updateSlideState(swiperInstance);
      }
    }, 150); // 150ms debounce
  }

  /**
   * Sets up protection to prevent other scripts from clearing the carousel's header text
   */
  function setupHeaderTextProtection() {
    const headerTextContent = document.getElementById('header-text-content');
    if (!headerTextContent) return;

    // Override HeaderText utility if it exists to ignore clear calls on homepage
    if (window.HeaderText) {
      const originalUpdate = window.HeaderText.update;
      const originalClear = window.HeaderText.clear;

      window.HeaderText.update = function(text, link) {
        // Only allow updates from carousel (via updateHeaderText function)
        // Other scripts should not modify header text on homepage
        if (carouselState.isActive && text !== carouselState.headerText) {
          // Ignore external updates, keep carousel's text
          return;
        }
        originalUpdate.call(this, text, link);
      };

      window.HeaderText.clear = function() {
        // Ignore clear calls when carousel is active
        if (carouselState.isActive) {
          return;
        }
        originalClear.call(this);
      };
    }

    // Also use MutationObserver as a fallback to catch any direct DOM modifications
    const observer = new MutationObserver(function(mutations) {
      if (!carouselState.isActive) return;
      
      mutations.forEach(function(mutation) {
        if (mutation.type === 'childList' || mutation.type === 'characterData') {
          // Check if the content was cleared by something else
          const currentText = headerTextContent.textContent.trim();
          if (carouselState.headerText && currentText !== carouselState.headerText) {
            // Re-apply the carousel's header text
            applyHeaderText();
          }
        }
      });
    });

    observer.observe(headerTextContent, {
      childList: true,
      characterData: true,
      subtree: true
    });
  }

  /**
   * Applies the stored header text state to the DOM
   */
  function applyHeaderText() {
    const headerTextContent = document.getElementById('header-text-content');
    const headerTextContainer = document.getElementById('header-text-container');
    const mobileFooterTextContent = document.getElementById('mobile-footer-bar-text-content');
    
    if (!headerTextContent) return;

    if (carouselState.headerTextLink && carouselState.headerText) {
      headerTextContent.innerHTML = '<a href="' + carouselState.headerTextLink + '" class="header-text__link">' + carouselState.headerText + '</a>';
    } else {
      headerTextContent.textContent = carouselState.headerText;
    }
    
    if (headerTextContainer) {
      if (carouselState.headerText && carouselState.headerText.trim() !== '') {
        headerTextContainer.style.display = '';
      } else {
        headerTextContainer.style.display = 'none';
      }
    }
    
    // Sync with mobile footer bar
    if (mobileFooterTextContent) {
      if (carouselState.headerTextLink && carouselState.headerText) {
        mobileFooterTextContent.innerHTML = '<a href="' + carouselState.headerTextLink + '" class="mobile-footer-bar__text-link">' + carouselState.headerText + '</a>';
      } else {
        mobileFooterTextContent.textContent = carouselState.headerText;
      }
    }
    
    // Dispatch event for any other components that need to sync
    document.dispatchEvent(new CustomEvent('headerTextUpdated', { 
      detail: { text: carouselState.headerText } 
    }));
  }

  /**
   * Updates all header state based on active slide
   */
  function updateSlideState(swiper) {
    // In loop mode, Swiper duplicates slides. We need to get the currently visible slide.
    // The .swiper-slide-active class is on the correct visible slide after transition ends.
    let activeSlide = swiper.el.querySelector('.swiper-slide-active');
    
    // If we can't find the active slide by class, use realIndex
    // realIndex gives the correct index of the original slide (not duplicate)
    if (!activeSlide) {
      // Get all original slides (not duplicates) - they don't have swiper-slide-duplicate class
      const originalSlides = swiper.el.querySelectorAll('.swiper-slide:not(.swiper-slide-duplicate)');
      if (originalSlides.length > 0 && swiper.realIndex < originalSlides.length) {
        activeSlide = originalSlides[swiper.realIndex];
      }
    }
    
    // Final fallback
    if (!activeSlide) {
      activeSlide = swiper.slides[swiper.activeIndex];
    }
    
    if (!activeSlide) return;

    updateLogoColor(activeSlide);
    updateHeaderColor(activeSlide);
    updateHeaderText(activeSlide);
  }

  /**
   * Checks if we're on mobile and slide style is inset/small-inset
   * Returns true if colors should be forced to black
   */
  function shouldForceBlackColors(activeSlide) {
    const isMobile = window.matchMedia('(max-width: 749px)').matches;
    const slideStyle = activeSlide.getAttribute('data-slide-style') || '';
    const isInsetStyle = slideStyle === 'inset' || slideStyle === 'small-inset';
    return isMobile && isInsetStyle;
  }

  /**
   * Updates logo color based on slide's logo color setting
   * On mobile with inset/small-inset styles, forces black
   */
  function updateLogoColor(activeSlide) {
    let logoColor = activeSlide.getAttribute('data-logo-color') || 'black';
    
    // Force black on mobile for inset styles
    if (shouldForceBlackColors(activeSlide)) {
      logoColor = 'black';
    }
    
    carouselState.logoColor = logoColor;
    
    const headerComponent = document.querySelector('header-component');
    const header = document.querySelector('.header');
    
    if (headerComponent) {
      headerComponent.setAttribute('data-carousel-logo-color', logoColor);
    }
    
    if (header) {
      header.setAttribute('data-carousel-logo-color', logoColor);
    }
  }

  /**
   * Updates header text color based on slide's header color setting
   * On mobile with inset/small-inset styles, forces black
   */
  function updateHeaderColor(activeSlide) {
    let headerColor = activeSlide.getAttribute('data-header-color') || 'black';
    
    // Force black on mobile for inset styles
    if (shouldForceBlackColors(activeSlide)) {
      headerColor = 'black';
    }
    
    carouselState.headerColor = headerColor;
    
    const headerComponent = document.querySelector('header-component');
    const header = document.querySelector('.header');
    const mobileFooterBar = document.getElementById('mobile-footer-bar');
    
    if (headerComponent) {
      headerComponent.setAttribute('data-carousel-header-color', headerColor);
    }
    
    if (header) {
      header.setAttribute('data-carousel-header-color', headerColor);
    }
    
    // Update mobile footer bar color
    if (mobileFooterBar) {
      mobileFooterBar.setAttribute('data-color', headerColor);
    }
  }

  /**
   * Updates header text based on slide's header text setting
   */
  function updateHeaderText(activeSlide) {
    const headerText = activeSlide.getAttribute('data-header-text') || '';
    const headerTextLink = activeSlide.getAttribute('data-header-text-link') || '';
    
    // Store in state
    carouselState.headerText = headerText;
    carouselState.headerTextLink = headerTextLink;
    
    // Apply to DOM
    applyHeaderText();
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCarousel);
  } else {
    initCarousel();
  }
})();
